/**
 * English: Chat composer — mode selector, station/device context, and send handler props.
 * Thai: ช่องพิมพ์แชท — เลือกโหมด, บริบทสถานี/อุปกรณ์ และ handler ส่งข้อความ
 */
import { useRef, useState } from 'react';
import {
  PaperAirplaneIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
  ChatBubbleLeftRightIcon,
} from '@heroicons/react/24/outline';
import LoadingSpinner from '../shared/LoadingSpinner';

export type ChatInputMode = 'ask' | 'control';

interface ChatInputProps {
  onSend: (message: string) => void;
  disabled: boolean;
  /** ห้าม Control mode (Master) */
  noControl?: boolean;
  /** ห้าม Control mode (RBAC — viewer / role without control permission) */
  controlDisabled?: boolean;
  inputMode: ChatInputMode;
  onInputModeChange: (mode: ChatInputMode) => void;
  placeholder?: string;
  /** ชุดข้อความสำหรับปุ่มสุ่มคำถาม — แสดงเฉพาะโหมดถาม */
  randomQuestionPool?: string[];
  /** เมื่อกำลังสตรีมงาน — ปุ่มขวาเป็นหยุดแทนส่ง (ปุ่มเดียวทั้งสองหน้าที่) */
  stopVisible?: boolean;
  onStop?: () => void;
  stopLoading?: boolean;
}

export default function ChatInput({
  onSend,
  disabled,
  noControl = false,
  controlDisabled = false,
  inputMode,
  onInputModeChange,
  placeholder,
  randomQuestionPool,
  stopVisible = false,
  onStop,
  stopLoading = false,
}: ChatInputProps) {
  const blockControl = noControl || controlDisabled;
  const [value, setValue] = useState('');
  const lastSubmitRef = useRef<{ value: string; at: number } | null>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const message = value.trim();
    if (!message || disabled) return;
    const now = Date.now();
    const last = lastSubmitRef.current;
    if (last && last.value === message && now - last.at < 750) return;
    lastSubmitRef.current = { value: message, at: now };
    onSend(message);
    setValue('');
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
  };

  const defaultPlaceholder =
    inputMode === 'control'
      ? 'พิมพ์คำสั่งควบคุม… · Device command (JWT)…'
      : 'ถามคำถาม คุย หรือขอข้อมูลเชิงลึก… · Ask, chat, or insights…';

  const sendAccent =
    inputMode === 'control'
      ? 'bg-amber-500 hover:bg-amber-500/80'
      : 'bg-purple-500 hover:bg-purple-500/80';

  const showStopInsteadOfSend = Boolean(stopVisible && onStop);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex bg-app-muted border border-app-border rounded-lg p-0.5 gap-0.5">
          <button
            type="button"
            onClick={() => onInputModeChange('ask')}
            title="ถาม · Ask (chat & insights)"
            className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 whitespace-nowrap ${inputMode === 'ask' ? 'bg-purple-500 text-white' : 'text-app-text-dim hover:text-app-text'
              }`}
          >
            <ChatBubbleLeftRightIcon className="w-3 h-3 shrink-0" />
            ถาม
          </button>
          {!blockControl && (
            <button
              type="button"
              onClick={() => onInputModeChange('control')}
              title="ควบคุม — ใช้ Bearer JWT; Manager=กราฟสถานี; Device=ตรงเครื่อง | Control uses JWT; Manager=station graph; Device=direct"
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-colors flex items-center gap-1 whitespace-nowrap ${inputMode === 'control' ? 'bg-amber-500 text-white' : 'text-app-text-dim hover:text-app-text'
                }`}
            >
              <ExclamationTriangleIcon className="w-3 h-3 shrink-0" />
              ควบคุม
            </button>
          )}
        </div>

        {inputMode === 'control' && (
          <span className="text-xs text-amber-600 dark:text-amber-400">
            ล็อกอินแล้วสั่งได้ทันที · Bearer JWT (ไม่มี OTP แยก)
          </span>
        )}

        {inputMode === 'ask' && randomQuestionPool && randomQuestionPool.length > 0 && (
          <button
            type="button"
            onClick={() => {
              const i = Math.floor(Math.random() * randomQuestionPool.length);
              setValue(randomQuestionPool[i]);
            }}
            disabled={disabled}
            title={`สุ่มจากชุดทดสอบ ${randomQuestionPool.length} คำถาม`}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-md border border-app-border text-app-text-dim hover:text-app-text hover:bg-app-muted disabled:opacity-50 transition-colors"
          >
            <ArrowPathIcon className="w-3.5 h-3.5" />
            สุ่มคำถาม
          </button>
        )}
      </div>

      <form onSubmit={handleSubmit} className="flex gap-3 items-end">
        <textarea
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder || defaultPlaceholder}
          rows={2}
          className="flex-1 bg-app-bg border border-app-border rounded-lg px-3 py-2 text-sm text-app-text placeholder:text-app-text-dim focus:ring-1 focus:ring-app-accent outline-none resize-none disabled:opacity-50"
          disabled={disabled}
        />
        <div className="flex shrink-0 items-end">
          {showStopInsteadOfSend ? (
            <button
              type="button"
              onClick={onStop}
              disabled={stopLoading}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[42px] min-w-[7rem] rounded-lg text-sm font-medium text-white bg-red-500 hover:bg-red-600 disabled:opacity-50 transition-colors whitespace-nowrap"
            >
              {stopLoading ? <LoadingSpinner size="sm" /> : <span>⛔ หยุด</span>}
            </button>
          ) : (
            <button
              type="submit"
              disabled={disabled || !value.trim()}
              className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 min-h-[42px] min-w-[7rem] rounded-lg text-sm font-medium ${sendAccent} disabled:opacity-50 text-white transition-colors`}
            >
              {disabled ? <LoadingSpinner size="sm" /> : <PaperAirplaneIcon className="w-4 h-4" />}
              Send
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
